import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

const BookingContext = createContext(null);

const DAYS_OF_WEEK = [
  { id: 0, label: "Понедельник" },
  { id: 1, label: "Вторник" },
  { id: 2, label: "Среда" },
  { id: 3, label: "Четверг" },
  { id: 4, label: "Пятница" },
  { id: 5, label: "Суббота" },
  { id: 6, label: "Воскресенье" }
];

export { DAYS_OF_WEEK };

export function BookingProvider({ children }) {
  const [expertSchedules, setExpertSchedules] = useState({});
  const [bookings, setBookings] = useState([]);
  const [schedulesLoaded, setSchedulesLoaded] = useState(false);
  const [bookingsLoaded, setBookingsLoaded] = useState(false);

  useEffect(() => {
    loadAllSchedules();
    loadAllBookings();
  }, []);

  const loadAllSchedules = async () => {
    try {
      const { data, error } = await supabase
        .from("expert_schedules")
        .select("*")
        .order("day_id", { ascending: true });

      if (error) {
        console.warn("Failed to load schedules:", error);
        setSchedulesLoaded(true);
        return;
      }

      const grouped = {};
      (data || []).forEach((row) => {
        if (!grouped[row.expert_id]) {
          grouped[row.expert_id] = [];
        }
        grouped[row.expert_id].push({
          dayId: row.day_id,
          label: DAYS_OF_WEEK.find((d) => d.id === row.day_id)?.label || "",
          enabled: row.enabled,
          startHour: row.start_hour,
          endHour: row.end_hour
        });
      });
      setExpertSchedules(grouped);
    } catch (err) {
      console.warn("Schedule load error:", err);
    } finally {
      setSchedulesLoaded(true);
    }
  };

  const loadAllBookings = async () => {
    const token = localStorage.getItem("obstuchim_token");

    // 1. Попытка загрузки через наш Express / Prisma бэкенд
    if (token) {
      try {
        const res = await fetch("/api/consultations", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.consultations)) {
            const mapped = data.consultations.map((c) => {
              const dt = new Date(c.scheduledAt || Date.now());
              const dateStr = dt.toISOString().split("T")[0];
              const timeStr = `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;

              return {
                id: c.id,
                expertId: c.expertId,
                clientId: c.clientId,
                clientName: c.client?.name || "Клиент",
                expertName: c.expert?.name || "Эксперт",
                date: dateStr,
                time: timeStr,
                topic: c.topic || "Консультация",
                price: c.price,
                platformFee: c.platformFee,
                expertEarnings: c.expertEarnings,
                status: c.status ? c.status.toLowerCase() : "confirmed",
                createdAt: c.createdAt,
                expertNotes: c.expertNotes,
                clientPrepBrief: c.clientPrepBrief,
                sessionDeck: c.sessionDeck,
              };
            });

            setBookings(mapped);
            setBookingsLoaded(true);
            return;
          }
        }
      } catch (apiErr) {
        console.warn("[BookingContext] /api/consultations fetch failed, trying fallback:", apiErr);
      }
    }

    // 2. Fallback через Supabase
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        console.warn("Failed to load bookings:", error);
        setBookingsLoaded(true);
        return;
      }

      const mapped = (data || []).map((row) => ({
        id: row.id,
        expertId: row.expert_id,
        clientId: row.client_id,
        clientName: row.client_name,
        expertName: row.expert_name,
        date: row.date,
        time: row.time,
        topic: row.topic,
        price: row.price,
        platformFee: row.platform_fee,
        expertEarnings: row.expert_earnings,
        status: row.status,
        createdAt: row.created_at
      }));
      setBookings(mapped);
    } catch (err) {
      console.warn("Bookings load error:", err);
    } finally {
      setBookingsLoaded(true);
    }
  };

  const setSchedule = useCallback(async (expertId, schedule) => {
    setExpertSchedules((prev) => ({ ...prev, [expertId]: schedule }));

    const token = localStorage.getItem("obstuchim_token");
    if (token) {
      try {
        await fetch(`/api/experts/${expertId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ calendarSlots: schedule }),
        });
      } catch (apiErr) {
        console.warn("[setSchedule] API update failed:", apiErr);
      }
    }

    try {
      await supabase
        .from("expert_schedules")
        .delete()
        .eq("expert_id", expertId);

      const rows = schedule.map((day) => ({
        expert_id: expertId,
        day_id: day.dayId,
        enabled: day.enabled,
        start_hour: day.startHour,
        end_hour: day.endHour
      }));

      const { error } = await supabase
        .from("expert_schedules")
        .insert(rows);

      if (error) {
        console.error("Failed to save schedule to Supabase:", error);
      }
    } catch (err) {
      console.error("Schedule save error:", err);
    }
  }, []);

  const getSchedule = useCallback((expertId) => {
    return expertSchedules[expertId] || [];
  }, [expertSchedules]);

  const getAvailableSlots = useCallback((expertId, date) => {
    const schedule = expertSchedules[expertId] || [];
    const dayOfWeek = (new Date(date).getDay() + 6) % 7;
    const daySchedule = schedule.find((s) => s.dayId === dayOfWeek);
    if (!daySchedule || !daySchedule.enabled) return [];

    const slots = [];
    const startHour = daySchedule.startHour;
    const endHour = daySchedule.endHour;
    for (let h = startHour; h < endHour; h++) {
      const slotTime = `${String(h).padStart(2, "0")}:00`;
      const isBooked = bookings.some(
        (b) =>
          b.expertId === expertId &&
          b.date === date &&
          b.time === slotTime &&
          b.status !== "cancelled"
      );
      slots.push({ time: slotTime, available: !isBooked });
    }
    return slots;
  }, [expertSchedules, bookings]);

  const hasAvailableSlots = useCallback((expertId, date) => {
    const schedule = expertSchedules[expertId] || [];
    const dayOfWeek = (new Date(date).getDay() + 6) % 7;
    const daySchedule = schedule.find((s) => s.dayId === dayOfWeek);
    if (!daySchedule || !daySchedule.enabled) return false;

    const startHour = daySchedule.startHour;
    const endHour = daySchedule.endHour;
    for (let h = startHour; h < endHour; h++) {
      const slotTime = `${String(h).padStart(2, "0")}:00`;
      const isBooked = bookings.some(
        (b) =>
          b.expertId === expertId &&
          b.date === date &&
          b.time === slotTime &&
          b.status !== "cancelled"
      );
      if (!isBooked) return true;
    }
    return false;
  }, [expertSchedules, bookings]);

  const createBooking = useCallback(async (booking) => {
    const tempId = `temp-${Date.now()}`;
    const newBooking = {
      ...booking,
      id: tempId,
      status: "confirmed",
      createdAt: new Date().toISOString()
    };
    setBookings((prev) => [...prev, newBooking]);

    const token = localStorage.getItem("obstuchim_token");
    if (token) {
      try {
        const scheduledAt = new Date(`${booking.date}T${booking.time || "12:00"}:00`).toISOString();
        const res = await fetch("/api/consultations", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            expertId: booking.expertId,
            scheduledAt,
            topic: booking.topic || "Консультация",
            price: booking.price,
          }),
        });
        if (res.ok) {
          const json = await res.json();
          if (json.consultation) {
            setBookings((prev) =>
              prev.map((b) => (b.id === tempId ? { ...b, id: json.consultation.id } : b))
            );
            return { ...newBooking, id: json.consultation.id };
          }
        }
      } catch (apiErr) {
        console.warn("[createBooking] Express API booking creation failed:", apiErr);
      }
    }

    try {
      const { data, error } = await supabase
        .from("bookings")
        .insert({
          expert_id: booking.expertId,
          client_id: booking.clientId,
          client_name: booking.clientName,
          expert_name: booking.expertName,
          date: booking.date,
          time: booking.time,
          topic: booking.topic || null,
          price: booking.price || null,
          platform_fee: booking.platformFee || null,
          expert_earnings: booking.expertEarnings || null,
          status: "confirmed"
        })
        .select()
        .single();

      if (!error && data) {
        setBookings((prev) =>
          prev.map((b) =>
            b.id === tempId ? { ...b, id: data.id } : b
          )
        );
      }
    } catch (err) {
      console.error("Booking create error:", err);
    }

    return newBooking;
  }, []);

  const completeBooking = useCallback(async (bookingId) => {
    setBookings((prev) =>
      prev.map((b) =>
        b.id === bookingId ? { ...b, status: "completed" } : b
      )
    );

    const token = localStorage.getItem("obstuchim_token");
    if (token && !bookingId.startsWith("temp-")) {
      try {
        await fetch(`/api/consultations/${bookingId}/status`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: "COMPLETED" }),
        });
      } catch (apiErr) {
        console.warn("[completeBooking] API status update warning:", apiErr);
      }
    }

    try {
      await supabase
        .from("bookings")
        .update({ status: "completed", updated_at: new Date().toISOString() })
        .eq("id", bookingId);
    } catch (err) {
      console.error("Complete booking error:", err);
    }
  }, []);

  const cancelBooking = useCallback(async (bookingId) => {
    setBookings((prev) =>
      prev.map((b) =>
        b.id === bookingId ? { ...b, status: "cancelled" } : b
      )
    );

    const token = localStorage.getItem("obstuchim_token");
    if (token && !bookingId.startsWith("temp-")) {
      try {
        await fetch(`/api/consultations/${bookingId}/status`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status: "CANCELLED" }),
        });
      } catch (apiErr) {
        console.warn("[cancelBooking] API status update warning:", apiErr);
      }
    }

    try {
      await supabase
        .from("bookings")
        .update({ status: "cancelled", updated_at: new Date().toISOString() })
        .eq("id", bookingId);
    } catch (err) {
      console.error("Cancel booking error:", err);
    }
  }, []);

  const getBookingsForExpert = useCallback((expertId) => {
    return bookings.filter(
      (b) => b.expertId === expertId && b.status !== "cancelled"
    );
  }, [bookings]);

  const getBookingsForClient = useCallback((clientId) => {
    return bookings.filter(
      (b) => b.clientId === clientId && b.status !== "cancelled"
    );
  }, [bookings]);

  return (
    <BookingContext.Provider
      value={{
        expertSchedules,
        bookings,
        schedulesLoaded,
        bookingsLoaded,
        setSchedule,
        getSchedule,
        getAvailableSlots,
        hasAvailableSlots,
        createBooking,
        completeBooking,
        cancelBooking,
        getBookingsForExpert,
        getBookingsForClient,
        refreshSchedules: loadAllSchedules,
        refreshBookings: loadAllBookings
      }}
    >
      {children}
    </BookingContext.Provider>
  );
}

export function useBooking() {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error("useBooking must be used within a BookingProvider");
  }
  return context;
}
